import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import PromoBanner from "@/components/site/PromoBanner";
import SEO from "@/components/seo/SEO";
import { CATEGORIES, products } from "@/data/products";

const PageHero = ({ kicker, title, intro }) => (
  <section className="bg-[#F7F7F4] border-b border-[#E7E7E2]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
      <div className="overline">— {kicker}</div>
      <h1 className="mt-5 font-sora text-[44px] sm:text-[60px] lg:text-[76px] font-extrabold tracking-tight leading-[1.02] text-[#111111]">
        {title}
      </h1>
      {intro && (
        <p className="mt-6 max-w-2xl font-manrope text-[17px] md:text-[19px] leading-[1.65] text-[#111111]/70">
          {intro}
        </p>
      )}
    </div>
  </section>
);

const ProductCard = ({ p }) => {
  const isAvailable = p.status === "available";
  return (
    <Link
      to={`/products/${p.slug}`}
      data-testid={`product-card-${p.slug}`}
      className="group bg-white flex flex-col min-h-[280px] transition-colors hover:bg-[#F7F7F4]"
    >
      <div className="aspect-video overflow-hidden border-b border-[#E7E7E2] bg-[#F7F7F4]">
        <img
          src={`/images/products/${p.slug}.jpg`}
          alt={`${p.name} cover`}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
        />
      </div>
      <div className="p-8 md:p-10 flex flex-col flex-1">
        <div className="flex items-center justify-between">
          <span className="font-mono text-xs text-[#111111]/45">{p.no}</span>
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#111111]/55">
            {p.category}
          </span>
        </div>
        <h3 className="mt-6 font-sora text-[26px] md:text-[28px] font-semibold text-[#111111] tracking-tight">
          {p.name}
        </h3>
        <p className="mt-3 font-manrope text-[15px] text-[#111111]/65 leading-relaxed">
          {p.tagline}
        </p>
        <div className="mt-auto pt-8 flex items-center justify-between">
          <span
            className={`font-mono text-[11px] tracking-[0.14em] uppercase ${
              isAvailable ? "text-[#2563EB]" : "text-[#111111]/40"
            }`}
          >
            {isAvailable ? "Available now" : "Coming soon"}
          </span>
          <ArrowUpRight
            size={18}
            className="text-[#111111]/30 group-hover:text-[#2563EB] transition-colors"
          />
        </div>
      </div>
    </Link>
  );
};

const Products = () => {
  const [params, setParams] = useSearchParams();
  const initial =
    CATEGORIES.find(
      (c) => c.toLowerCase().replace(/\s+/g, "-") === params.get("category")
    ) || "All";
  const [active, setActive] = useState(initial);

  const handleSetActive = (cat) => {
    setActive(cat);
    if (cat === "All") {
      params.delete("category");
    } else {
      params.set("category", cat.toLowerCase().replace(/\s+/g, "-"));
    }
    setParams(params, { replace: true });
  };

  const filtered =
    active === "All" ? products : products.filter((p) => p.category === active);

  return (
    <main
      data-testid="products-page"
      className="min-h-screen bg-[#F7F7F4] flex flex-col"
    >
      <SEO
        title="Products"
        description="Every Vaymilo digital kit — AI Kits, Templates, and Workflows. Built to be useful from the first minute."
        path="/products"
      />
      <PromoBanner />
      <Header />
      <PageHero
        kicker="Products"
        title="Every kit Vaymilo makes."
        intro="A small, growing catalog of practical AI kits, templates, and workflows. Each one is built to be useful from the first minute — no learning curve, no fluff."
      />

      <section className="bg-white border-b border-[#E7E7E2]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-12">
          <div
            data-testid="category-filter"
            className="flex flex-wrap gap-2 md:gap-3"
          >
            {CATEGORIES.map((c) => {
              const isActive = c === active;
              return (
                <button
                  key={c}
                  onClick={() => handleSetActive(c)}
                  data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-4 md:px-5 py-2 rounded-full font-manrope text-sm border transition-colors ${
                    isActive
                      ? "bg-[#111111] text-white border-[#111111]"
                      : "bg-white text-[#111111]/75 border-[#E7E7E2] hover:border-[#111111] hover:text-[#111111]"
                  }`}
                >
                  {c}
                </button>
              );
            })}
            <span className="ml-auto self-center font-mono text-[11px] tracking-[0.16em] uppercase text-[#111111]/45">
              {filtered.length} {filtered.length === 1 ? "item" : "items"}
            </span>
          </div>
        </div>
      </section>

      <section className="flex-1 bg-[#F7F7F4]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          {filtered.length === 0 ? (
            <div
              data-testid="products-empty"
              className="font-manrope text-[15px] text-[#111111]/60 py-16 text-center"
            >
              Nothing in this category yet — check back soon.
            </div>
          ) : (
            <div
              data-testid="products-grid"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E7E7E2] border border-[#E7E7E2] rounded-2xl overflow-hidden"
            >
              {filtered.map((p) => (
                <ProductCard key={p.slug} p={p} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Products;
